// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartUtils.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
!(function() {
  var React = require('react');
  var m = require('mobx');
  var LazyLoadingBar = require('share/components/controls/LazyLoading/Izenda.LazyLoadingBar');
  var convertToDateTimeFormat = require('share/utils/Izenda.ConvertToDateTimeFormat');
  var convertToNumeralFormat = require('share/utils/Izenda.ConvertToNumeralFormat');
  var routeUtils = require('share/utils/Izenda.RouteUtils');
  var ReportPartElement = require('logic/models/Izenda.ReportPartElement');
  var ReportDesignerConstants = require('logic/data/Izenda.ReportDesignerConstants');
  var ReportPartGridConstants = require('logic/data/Izenda.ReportPartGridConstants');
  var ReportPartChartConstants = require('logic/data/Izenda.ReportPartChartConstants');
  var commonDataUtils = require('logic/Izenda.ReportDesignerCommonDataUtils');
  var reportPartElementUtils = require('share/utils/Izenda.ReportPartElementUtils');
  var uiState = require('logic/Izenda.UiState');

  var fetcher = require('fetchers/Izenda.ReportsDataFetcher');
  var validateExistingReport = fetcher.validateExistingReport;
  var validateExistingField = fetcher.validateExistingField;
  var cacheUtils = require('share/utils/Izenda.CacheUtils');

  var moment = require('moment');
  var ReportBodyConstants = require('logic/data/Izenda.ReportBodyConstants');
  var MESSAGE = ReportBodyConstants.MESSAGES;
  var ReportPartFieldConstants = require('logic/data/Izenda.ReportPartFieldConstants');
  var DEFAULT_FORMAT_BY_FUNCTION_DATA_TYPE = ReportPartFieldConstants.DEFAULT_FORMAT_BY_FUNCTION_DATA_TYPE;
  var DEFAULT_FORMAT_BY_DATA_TYPE = ReportPartFieldConstants.DEFAULT_FORMAT_BY_DATA_TYPE;
  var FIELD_FUNCTION_MODE = ReportPartFieldConstants.FIELD_FUNCTION_MODE;
  var FUNCTION = ReportPartFieldConstants.FUNCTION;
  var FUNCTION_GROUP_NAME = ReportPartFieldConstants.FUNCTION.GROUP.NAME;
  var TimeFormatConstants = require('logic/data/Izenda.TimeFormatConstants');
  var NumericAndMoneyFormatConstants = require('logic/data/Izenda.NumericAndMoneyFormatConstants');
  var DATE_TIME_FORMAT = TimeFormatConstants.DATE_TIME_FORMAT;
  var DATE_TIME_GROUP_BY = TimeFormatConstants.GROUP_BY;
  var FORMATS_NOT_AVAILABLE_FOR_RUNNING_FIELD = NumericAndMoneyFormatConstants.FORMATS_NOT_AVAILABLE_FOR_RUNNING_FIELD;
  var FORMATS_NOT_AVAILABLE_FOR_PART_TYPES = NumericAndMoneyFormatConstants.FORMATS_NOT_AVAILABLE_FOR_PART_TYPES;
  var FORMATS_ONLY_ON_PIVOT_GRID_VALUE_CONTAINER =
    NumericAndMoneyFormatConstants.FORMATS_ONLY_ON_PIVOT_GRID_VALUE_CONTAINER;
  var FORMAT_NOT_AVAILABLE_ON_FIELD_CAN_NOT_SET_SUBTOTAL =
    NumericAndMoneyFormatConstants.FORMAT_NOT_AVAILABLE_ON_FIELD_CAN_NOT_SET_SUBTOTAL;
  var DATA_TYPES = ReportDesignerConstants.DATA_TYPES;
  var REPORT_PART_TYPE = ReportDesignerConstants.REPORT_PART_TYPE;
  var GRID_STYLES = ReportPartGridConstants.GRID_STYLES;
  var GRID_CONTAINER_NAME = ReportPartGridConstants.CONTAINER_NAME;
  var AppGlobalConstants = require('logic/data/Izenda.AppGlobalConstants');
  var TIME_FORMAT = AppGlobalConstants.TIME_FORMAT;
  var EMPTY_GUID = require('logic/data/Izenda.ReportsConstants').EMPTY_GUID;
  var GridDefaultConfig = require('logic/data/Izenda.GridDefaultConfig');
  var ChartDefaultConfig = require('logic/data/Izenda.ChartDefaultConfig');
  var FormDefaultConfig = require('logic/data/Izenda.FormDefaultConfig');
  var AppGlobal = require('Izenda.AppGlobal');
  var config = require('Izenda.Config');


  var COLUMN_NAME_PREFIX = ReportDesignerConstants.COLUMN_NAME_PREFIX;

  var cacheOption = {
    persistent: true,
    resolver: function() {
      return JSON.stringify(arguments);
    }
  };

  var getFunctionData = cacheUtils.memoize(commonDataUtils.getFunctionData, cacheOption);
  var getFormatData = cacheUtils.memoize(commonDataUtils.getFormatData, cacheOption);
  var getFunction = cacheUtils.memoize(commonDataUtils.getFunction, cacheOption);
  const {
    toString,
    head,
    tail,
    invoke,
    map,
    mapValues,
    isUndefined,
    curry,
    concat,
    escapeRegExp,
    toNumber,
    isEmpty,
    find,
    isNil,
    assign,
    flow,
    pick,
    forEach,
    without,
    isEqual,
    flatMap,
    uniqWith,
    get,
    getOr,
    includes,
    inRange,
    nth,
    has,
    filter,
    first,
    isBoolean,
    remove,
    split,
    last,
    endsWith,
    startsWith,
    invokeArgs,
    isString,
    omit,
    each,
    findIndex,
    some,
    indexOfFrom,
    reduce,
    isNaN,
    isObject
  } = require('lodash/fp');

  function buildDataSourceElement(reportPart, element) {
    return {
      columnName: flow(
        find({
          fieldNameAlias: element.field.fieldNameAlias
        }),
        get('columnName')
      )(reportPart.previewDataFieldsMapping),
      reportPartElm: element
    };
  }

  /**
   * get unique name for element
   * @method getUniqueName
   */
  function getUniqueName(reportPartContent, element, originalName) {
    var elements = flow(
      flatMap(
        flow(
          get('elements'),
          invoke('peek')
        )
      ),
      without([element])
    )(reportPartContent.containerCollection);

    return String.generateNewName(originalName, elements, get('name'), '()');
  }

  /**
   * get new name after apply new function
   * @method getNewElementName
   */
  function getNewElementName(element, functionInfo) {
    var functionName = getOr('', 'name', functionInfo);
    var originalFunctionName = getOr('', 'properties.dataFormattings.functionInfo.name', element);
    var regexString = new RegExp(
      String.format(ReportBodyConstants.FIELD_NAME_ALIAS_REGEX, escapeRegExp(originalFunctionName))
    );
    var originalFieldNameAlias = element.name;

    if (regexString.test(originalFieldNameAlias)) {
      var matchGroup = regexString.exec(originalFieldNameAlias);

      originalFieldNameAlias = matchGroup[2] || matchGroup[3];
    }

    return !isEmpty(functionName) && functionName.indexOf('[') < 0 && functionName !== FUNCTION.GROUP.NAME
      ? String.format(ReportBodyConstants.FIELD_NAME_WITH_FUNCTION_TEMPLATE, functionName, originalFieldNameAlias)
      : originalFieldNameAlias;
  }

  function buildFilterDataSubReport(records, subReportMappingFields, previewDataFieldMapping) {
    var result = { success: true, filters: [] };
    var data = flow(
      m.toJS,
      map(mapValues(toString))
    )(records);

    result.filters = map(function(mappingField) {
      var selectedFieldMapping = find(function(fieldMapping) {
        return fieldMapping.fieldNameAlias === mappingField.fieldNameAlias;
      }, previewDataFieldMapping);

      if (!selectedFieldMapping) {
        result.success = false;
      }
      return {
        key: mappingField.fieldNameInSubReport,
        fieldNameAlias: mappingField.fieldNameAlias,
        columnName: get('columnName', selectedFieldMapping),
        linkReportFilter: true
      };
    }, subReportMappingFields);
    if (!result.success) {
      return result;
    }
    var listColumnName = map('columnName', result.filters);

    data = flow(
      map(pick(listColumnName)),
      uniqWith(isEqual)
    )(data);

    if (data.length !== 1) {
      result.success = false;
      return result;
    }

    data = head(data);

    result.filters = map(function(filterItem) {
      const value = get(filterItem.columnName, data);
      const encodedValue = config.Integration ? encodeURIComponent(value) : value;

      return assign(filterItem, { value: encodedValue });
    }, result.filters);

    return result;
  }

  /**
   * set function for element
   * @method setFunctionElement
   */
  function setFunctionElement(reportPartContent, element, functionInfo, manualChange, keepOriginalName) {
    var newName = getNewElementName(element, functionInfo);
    var uniqueNewName = getUniqueName(reportPartContent, element, newName);

    !keepOriginalName && element.setName(uniqueNewName);
    element.properties.setFunction(functionInfo);

    return Promise.resolve(manualChange && invokeArgs('afterUpdateFunction', [element], reportPartContent));
  }

  /**
   * get default function for element
   * @method getDefaultFunctionFormat
   */
  function getDefaultFunctionFormat(reportPartContent, container, element) {
    var dataType = element.fieldDataType;
    var promiseFunction = Promise.resolve(null);
    var promiseFormat = Promise.resolve(null);

    if (!reportPartContent.getDefaultFunctionFormat || element.field.hasAggregatedFunction) {
      return getFormatData(dataType, getOr('', dataType, DEFAULT_FORMAT_BY_DATA_TYPE)).then(function(formatData) {
        return {
          functionData: {},
          formatData: formatData || {},
          defaultFunctionConfig: {}
        };
      });
    }

    var defaultData = reportPartContent.getDefaultFunctionFormat(container, dataType) || {
      FORMAT: { NAME: getOr('', dataType, DEFAULT_FORMAT_BY_DATA_TYPE) }
    };

    promiseFunction = getFunctionData(
      FIELD_FUNCTION_MODE.FieldProperty,
      dataType,
      getOr('', 'FUNCTION.NAME', defaultData)
    );
    promiseFormat = getFormatData(dataType, getOr('', 'FORMAT.NAME', defaultData));

    return Promise.join(promiseFunction, promiseFormat, function(functionData, formatData) {
      return { functionData: functionData, formatData: formatData, defaultFunctionConfig: defaultData };
    });
  }

  function getDefaultFormat(configField, functionInfo) {
    functionInfo = functionInfo || configField.getValueByKey('functionInfo') || {};
    var dataType = functionInfo.formatDataType || configField.getValueByKey('dataFieldType');
    var functionName = getOr('', 'name', functionInfo);
    var defaultFormatName =
      get([dataType, functionName].join('.'), DEFAULT_FORMAT_BY_FUNCTION_DATA_TYPE) ||
      get(dataType, DEFAULT_FORMAT_BY_DATA_TYPE);

    if (defaultFormatName) {
      return getFormatData(dataType, defaultFormatName);
    }
    return Promise.resolve();
  }

  /**
   * set default function for element
   * @method setDefaultFunctionElement
   */
  function setDefaultFunctionElement(reportPartContent, container, element) {
    return getDefaultFunctionFormat(reportPartContent, container, element).then(function(data) {
      data.formatData.id &&
        element.properties.setFormat({
          formatId: data.formatData.customId,
          format: data.formatData.format,
          groupBy: data.formatData.groupBy,
          formatDataType: data.formatData.formatDataType
        });
      return setFunctionElement(reportPartContent, element, data.functionData, true);
    });
  }

  /**
   * set function for element without function
   * @method convertNonFunctionElementsToFunction
   * @param  {object}                             reportPartContent [description]
   * @param  {string}                             newFunctioName    !optional if don't provide use default function
   * @return {Promise}                                              [description]
   */
  function convertNonFunctionElementsToFunction(reportPartContent, newFunctioName) {
    return flow(
      flatMap(function(container) {
        return flow(
          filter({ haveFunction: false }),
          map(function(element) {
            if (newFunctioName) {
              var defaultFormatName = get(
                [element.fieldDataType, newFunctioName].join('.'),
                DEFAULT_FORMAT_BY_FUNCTION_DATA_TYPE
              );
              var defaultFormatPromise = Promise.resolve({});

              if (defaultFormatName) {
                defaultFormatPromise = getFormatData(element.fieldDataType, defaultFormatName);
              }
              return Promise.join(
                defaultFormatPromise,
                getFunctionData(FIELD_FUNCTION_MODE.FieldProperty, element.fieldDataType, newFunctioName),
                m.action(function(formatData, functionData) {
                  formatData.id &&
                    element.properties.setFormat({
                      formatId: formatData.id,
                      format: formatData.format,
                      groupBy: formatData.groupBy,
                      formatDataType: formatData.formatDataType
                    });
                  return setFunctionElement(reportPartContent, element, functionData);
                })
              );
            }
            return setDefaultFunctionElement(reportPartContent, container, element);
          })
        )(container.elements.peek());
      }),
      Promise.all
    )(reportPartContent.containerCollection);
  }

  // common check for function with have config object
  function checkCompatibleFunctionCommon(functionInfo, element, config) {
    var result = true;
    var hasAggregatedFunction = element.field.hasAggregatedFunction;
    var functionName = getOr(ReportPartFieldConstants.EMPTY_FUNCTION.name, 'name', functionInfo);

    if (functionInfo.userDefined) {
      // if function is userDefined treat it like group function
      functionName = FUNCTION.GROUP.NAME;
    }

    if (config.SUPPORTED) {
      var supportedList = config.SUPPORTED[element.fieldDataType] || config.SUPPORTED.DEFAULT;
      var supportedFunction = map('NAME', supportedList);

      hasAggregatedFunction &&
        (supportedFunction = concat([ReportPartFieldConstants.EMPTY_FUNCTION.name], supportedFunction));
      result = includes(functionName, supportedFunction);
    }
    if (config.UNSUPPORTED) {
      var unsupportedList = config.UNSUPPORTED[element.fieldDataType] || config.UNSUPPORTED.DEFAULT;
      var unsupportedFunction = map('NAME', unsupportedList);

      hasAggregatedFunction &&
        (unsupportedFunction = without([ReportPartFieldConstants.EMPTY_FUNCTION.name], unsupportedFunction));
      result = result && !includes(functionName, unsupportedFunction);
    }
    return result;
  }

  /**
   * @method checkedCompatibleFunctionHasAggregatedFunction
   * @param  {object}                                       functionInfo [description]
   * @return {Boolean}                                                    [description]
   */
  function checkedCompatibleFunctionHasAggregatedFunction(functionInfo) {
    return !functionInfo.name || functionInfo.userDefined;
  }

  /**
   * Apply data format to value
   * @param  {any} fieldValue
   * fieldValue {
        originalValue:
        value:
      }
   * @param  {object} fieldConfig
   * @param  {array} dataSource
   * @param  {array} isApplyAlternativeText
   * @return {string}
   */
  function getDataFormat(fieldValue: any, fieldConfig: Object, dataSource: any, isApplyAlternativeText: Boolean) {
    const { value, originalValue } = isObject(fieldValue) ? fieldValue : { value: fieldValue, originalValue: null };

    if (isNil(fieldConfig)) {
      return value;
    }

    // apply alternative text by default
    isApplyAlternativeText = !isBoolean(isApplyAlternativeText) || isApplyAlternativeText;

    const valueForAlternativeText = !isNil(originalValue) ? originalValue : value;
    const alternativeText =
      isApplyAlternativeText &&
      getAlternativeText(valueForAlternativeText, fieldConfig, dataSource);

    return alternativeText || applyFormater(fieldConfig.fieldDataType, fieldConfig.fieldFormatData, value, dataSource);
  }

  function applyFormater(dataType, fieldFormat, value, dataSource) {
    if (dataType === DATA_TYPES.TIME) {
      fieldFormat = fieldFormat || TIME_FORMAT;
    }

    if (!fieldFormat) {
      return value;
    }

    var fnFormatter;
    var inputFormat;

    switch (dataType) {
      case DATA_TYPES.NUMERIC:
      case DATA_TYPES.MONEY:
        fnFormatter = convertToNumeralFormat;
        break;
      case DATA_TYPES.TIME:
      case DATA_TYPES.DATE_TIME:
        inputFormat = get('element.reportPartElm.dateTimeFormatData.dataFormat', dataSource);
        var dateConfig = DATE_TIME_FORMAT[fieldFormat];

        if (
          dateConfig &&
          dateConfig.FOR_DATE_SETTING &&
          !includes(uiState.currentUser.dateFormat, dateConfig.FOR_DATE_SETTING)
        ) {
          var groupBy = dateConfig.GROUP_BY;

          fieldFormat = DATE_TIME_GROUP_BY[groupBy].DEFAULT_FORMAT[uiState.currentUser.dateFormat];
        }
        fnFormatter = convertToDateTimeFormat;
        break;
      default:
      // do nothing
    }

    return fnFormatter ? fnFormatter(fieldFormat, value, null, inputFormat) : value;
  }

  function valueInRange(checkValue, item) {
    return inRange(toNumber(item.from), toNumber(item.to), checkValue) || checkValue === toNumber(item.to);
  }

  function getConfigDataByRange(value, configData, dataSource) {
    if (isNil(value) || !configData) {
      return null;
    }

    var fieldElement = get('element', dataSource);
    var definedText = [];
    var dateObject = null;

    if (get('reportPartElm.previewDataType', fieldElement) === DATA_TYPES.TIME) {
      value = convertToDateTimeFormat(TIME_FORMAT, value, false);
    }

    if (configData.value && configData.value.length) {
      // config by value
      dateObject = checkDateValue();
      definedText = configData.value.filter(function(item) {
        if (dateObject) {
          // compare between string and int
          return dateObject.value == item.key;
        }
        return value.toString().toUpperCase() === item.key.toString().toUpperCase();
      });
    } else if (configData.rangeValue && configData.rangeValue.length) {
      // config by range value
      dateObject = checkDateValue();
      definedText = configData.rangeValue.filter(function(item) {
        if (dateObject) {
          if (item.from <= item.to) {
            return item.from <= dateObject.value && dateObject.value <= item.to;
          }
          return item.from <= dateObject.value || dateObject.value <= item.to;
        }
        return valueInRange(toNumber(value), item);
      });
    } else if (configData.rangePercent && configData.rangePercent.length && has('record', dataSource)) {
      // config by range percent
      var valueByPercent = get(
        COLUMN_NAME_PREFIX.PERCENTAGE_VALUE + get('columnName', fieldElement),
        dataSource.record
      );

      definedText =
        !isNil(valueByPercent) &&
        configData.rangePercent.filter(function(item) {
          return valueInRange(toNumber(valueByPercent), item);
        });
    }

    return get('text', head(definedText));

    function checkDateValue() {
      var dateTimeFormatData = get('reportPartElm.dateTimeFormatData', fieldElement);

      if (dateTimeFormatData) {
        var dateObj =
          dateTimeFormatData.dataFormat === 'D' // special case for day, we don't want to moment js to  use current month,
            ? // because current month may have only 28 day for feb
              // forcing all case to make sure it will convert to same month if return date object for chart
              moment()
                .startOf('year')
                .date(value)
            : moment(value, dateTimeFormatData.dataFormat, true);

        if (dateObj.isValid()) {
          var formattedValue = dateObj.format(dateTimeFormatData.compareFormat);
          // Day of week on FE use start of day 0, BE use 1
          if (dateTimeFormatData.compareFormat === 'E') {
            var dow = parseInt(value);
            if (!isNaN(dow) && (dow >= 1 && dow <= 7)) {
              formattedValue = nth(value, TimeFormatConstants.DAY_OF_WEEK_VALUES);
            }
          }
          return { value: formattedValue, format: dateTimeFormatData.compareFormat };
        }
      }
      return null;
    }
  }

  /**
   * Get alternative text
   * @param  {string} value           value need change to alternative text
   * @param  {object} alternativeText alternative text settings
   * @param  {array} dataSource       list field source
   */
  function getAlternativeText(value, fieldConfig, dataSource) {
    if (!fieldConfig || !fieldConfig.alternativeText) {
      return null;
    }

    // special case for null values only for Text fields to treat null as "null"
    if (isNil(value) && fieldConfig.fieldDataType === DATA_TYPES.TEXT) {
      const convertNullToEmpty = AppGlobal.advancedSettings.other.convertNullToEmptyStringValue;
      value = String.commonStringValue(value, convertNullToEmpty);
    }

    return getConfigDataByRange.call(null, value, fieldConfig.alternativeText, dataSource);
  }

  function openHyperlinkAction(props, fieldConfig, record, CommonActionPopup) {
    var subReport = get('subReport', fieldConfig);
    var customURL = get('customURL', fieldConfig);
    var embeddedJavascript = get('embeddedJavascript', fieldConfig);
    var numberOfPointActions = get('numberOfPointActions', fieldConfig);

    if (numberOfPointActions === 1) {
      get('selectedReport', subReport) && routeUtils.openSubReport(props, subReport, record, fieldConfig);
      get('url', customURL) && openCustomURL(props, customURL, record, fieldConfig);
      get('script', embeddedJavascript) && performEmbeddedJavascript(props, embeddedJavascript, record, fieldConfig);
    } else if (numberOfPointActions > 1) {
      props.openPopup(CommonActionPopup, { fieldConfig: fieldConfig }).then(function(action) {
        action === 'openSubReport' && routeUtils.openSubReport(props, subReport, record, fieldConfig);
        action === 'openCustomURL' && openCustomURL(props, customURL, record, fieldConfig);
        action === 'performEmbeddedJavascript' &&
          performEmbeddedJavascript(props, embeddedJavascript, record, fieldConfig);
      });
    }
  }

  function openCustomURL(props, customURL, record, fieldConfig) {
    var reportPart = props.reportPart;
    var URL = customURL.url;
    var previewData = m.toJS(reportPart.previewData);
    var previewFieldsMapping = previewData.fieldsMapping;

    var httpReg = /^https?:\/\//i;
    var isExternal = httpReg.test(URL);

    return validateURL(reportPart, record, URL, isExternal, fieldConfig).then(function(msg) {
      if (!isEmpty(msg)) {
        return props.confirmMessage('error', msg.join(', '));
      }

      var mappingField;

      if (isExternal) {
        mappingField = getFilterMappingValuesWithExternalURL(record, previewFieldsMapping, URL, fieldConfig);
        routeUtils.openCustomURL(props, customURL, mappingField.filters, null, isExternal, fieldConfig);
      } else {
        mappingField = getFilterMappingValuesWithInternalURL(record, previewFieldsMapping, URL);
        var catAndReportName = findCatAndReportNameInUrl(URL);

        fetcher
          .getReportIdByNameAndCateName(catAndReportName.cat, catAndReportName.re, uiState.tenantId)
          .then(function(result) {
            if (result.data) {
              var reportId = result.data;

              routeUtils.openCustomURL(props, customURL, mappingField.filters, reportId, isExternal, fieldConfig);
            }
          });
      }
    });
  }

  /**
   * Validate and execute embedded javascript
   * @param {any} props
   * @param {any} embeddedJavascript
   * @param {any} record
   * @param {any} fieldConfig
   */
  function performEmbeddedJavascript(props: any, embeddedJavascript: any, record: any, fieldConfig: any) {
    const {
      reportPart,
      reportPart: {
        previewData: { fieldsMapping: previewFieldsMapping }
      }
    } = props;
    let script = embeddedJavascript.script;
    const httpReg = /^https?:\/\//i;

    // find all Urls in script
    const parseURlInScript = [];
    const regex = /urldecode<.*>/gi;
    const httpRegex = /(?:"|')https?:\/\/\S+(?:"|')/gi;
    let matched;

    while ((matched = regex.exec(script))) {
      parseURlInScript.push({
        url: matched[0].substring('urldecode<'.length, matched[0].length - 1),
        index: matched.index,
        length: matched[0].length
      });
    }

    // Find url in script
    // Ex: window.open("http://www.google.com/?q={[ShipCountry]}")
    // matched = '"http://www.google.com/?q={[ShipCountry]}"'
    while ((matched = httpRegex.exec(script))) {
      matched[0] = matched[0].substring(1, matched[0].length - 1);
      parseURlInScript.push({
        url: matched[0],
        index: matched.index + 1,
        length: matched[0].length
      });
    }

    Promise.map(parseURlInScript, ({ url }) => validateURL(reportPart, record, url, httpReg.test(url), fieldConfig))
      .then(result => {
        const validateMessage = [];
        for (let i = 0; i < result.length; i++) {
          !isEmpty(result[i]) && validateMessage.push(result[i].join(', '));
        }

        // show error message if one of url in script has error
        if (!isEmpty(validateMessage[0])) {
          return props.confirmMessage('error', validateMessage[0]).then(() => {
            throw new Error();
          });
        }
      })
      .then(() => {
        // build urls with params replaced
        const getURLLinkPromise = [];

        parseURlInScript.forEach((item, index) => {
          const url = item.url;
          const isExternal = httpReg.test(url);
          let mappingField;

          if (isExternal) {
            mappingField = getFilterMappingValuesWithExternalURL(record, previewFieldsMapping, url, fieldConfig);
            item.url = routeUtils.getCustomURLExternalLink(url, mappingField.filters);
          } else {
            mappingField = getFilterMappingValuesWithInternalURL(record, previewFieldsMapping, url);
            const catAndReportName = findCatAndReportNameInUrl(url);

            getURLLinkPromise.push(
              fetcher
                .getReportIdByNameAndCateName(catAndReportName.cat, catAndReportName.re, uiState.tenantId)
                .then(({ data: reportId }) => {
                  if (reportId) {
                    return {
                      index: index,
                      url: routeUtils.getCustomURLInternalLink(mappingField.filters, reportId)
                    };
                  }
                })
            );
          }
        });

        return Promise.all(getURLLinkPromise).then(result => {
          for (let i = 0; i < result.length; i++) {
            !isEmpty(result[i]) && (parseURlInScript[result[i].index].url = result[i].url);
          }
        });
      })
      .then(() => {
        parseURlInScript.forEach(item => {
          script = String.replaceAt(script, item.index, item.length, item.url);
        });
        const mappingField = getFilterMappingValuesWithExternalURL(record, previewFieldsMapping, script, fieldConfig);
        const formattedScript = routeUtils.getCustomURLExternalLink(script, mappingField.filters, fieldConfig);
        try {
          // NOSONAR
          new Function(formattedScript)();
        } catch (error) {
          return props.confirmMessage('error', MESSAGE.M11);
        }
      })
      .catch(function() {
        // do nothing
      });
  }

  function addContainers(source, containerName) {
    source[containerName] = {
      elements: [],
      name: containerName,
      visibleElement() {
        return filter('isVisible', this.elements);
      },
      numElement() {
        return this.visibleElement.length;
      }
    };
  }

  /**
   * Add in custom containers
   * @param {Array<any>} customContainers
   * @param {string} containerName
   * @returns {Object}
   */
  function addCustomContainers(customContainers: Array<any>, containerName: string): any {
    const container = {
      elements: [],
      name: containerName,
      visibleElement() {
        return filter('isVisible', this.elements);
      },
      numElement() {
        return this.visibleElement.length;
      }
    };

    // Cannot use push() here. It makes mobx becomes unstable.
    customContainers.splice(-1, 0, container);

    return container;
  }

  function hasElement() {
    var isAllContainer = first(arguments);
    var dataContainerArr;

    if (isBoolean(isAllContainer)) {
      dataContainerArr = tail(arguments) || [];
    } else {
      isAllContainer = true;
      dataContainerArr = arguments;
    }

    const count = reduce(
      function(re, dataContainer) {
        dataContainer = isString(dataContainer) ? this[dataContainer] : dataContainer;

        var visibleElement = dataContainer.visibleElement || dataContainer.elements;
        var hasElement = visibleElement && visibleElement.length;

        return isAllContainer ? re && hasElement : re || hasElement;
      }.bind(this),
      isAllContainer,
      dataContainerArr
    );

    return count > 0;
  }

  function hasAtLeastElement() {
    return hasElement.apply(this, concat([false], arguments));
  }

  function getElements(container, activeSerieKey, containerName) {
    var elms = (container && container.visibleElement) || [];
    var checkContainer = containerName ? containerName === get('name', container) : true;

    if (checkContainer && activeSerieKey) {
      return elms.filter(function(elm) {
        return elm.fieldName === activeSerieKey;
      });
    }

    return elms;
  }

  function populateReportChartContainer(data, name, callback) {
    var source = {};

    addContainers(source, name);

    if (!data) {
      return source;
    }

    var pickData = omit('elements', data);
    var elements = data.elements;
    var elm;

    !pickData.name && (pickData.name = name);

    /* convert elements json object to become list of ReportPartElement*/
    var convertElements = map(function(item) {
      elm = new ReportPartElement().readData(item);
      callback && callback(pickData, elm, item);

      return elm;
    }, elements);

    source[name].elements = convertElements || [];

    m.extendObservable(pickData, source[name]);

    return pickData;
  }

  function populateReportChartCustomContainer(data, customContainers) {
    each(container => {
      let customContainer = find({ name: container.name }, customContainers);
      if (isNil(customContainer)) {
        customContainer = addCustomContainers(customContainers, name);
      }

      /* convert elements json object to become list of ReportPartElement*/
      const convertElements = map(item => new ReportPartElement().readData(item), container.elements);

      customContainer.elements = convertElements;
    }, data);
  }

  function buildMappingFields(reportPartType, subReport, filters) {
    switch (reportPartType) {
      case 0: // chart
      case 2: // gauge
      case 4: // map
        return buildMappingFieldsForChart(subReport, filters);
      default:
        return filters;
    }
  }

  function buildMappingFieldsForChart(subReport, filters) {
    var mappingFields = getOr([], 'mappingFields', subReport);
    var mappingField;

    each(function(filterItem) {
      mappingField = findMappingField(filterItem.key, mappingFields);

      filterItem.key = mappingField ? mappingField.fieldNameInSubReport : null;
    }, filters);

    return remove(isNil, filters);

    function findMappingField(fieldSourceName, mappingFields) {
      return find(function(mappingField) {
        return fieldSourceName === mappingField.fieldNameAlias;
      }, mappingFields);
    }
  }

  function validateURL(reportPart, foundRecords, url, isExternal, fieldConfig) {
    var fieldCollection = reportPart.fieldCollection;
    var previewData = m.toJS(reportPart.previewData);
    var previewFieldsMapping = previewData.fieldsMapping;

    var errorMessage = [];
    var mappingValues;
    var currentReportColumns = findColumnInURL(url);
    var fieldNameNotFound = [];

    currentReportColumns.forEach(function(column) {
      findIndex({ fieldName: column }, fieldCollection) === -1 && fieldNameNotFound.push(column);
    });
    if (!isEmpty(fieldNameNotFound)) {
      errorMessage.push(String.format(MESSAGE.M12, fieldNameNotFound.join(', ')));
      return Promise.resolve(errorMessage);
    }

    // validate reportName
    // does column exists
    // Check whether it has many data values
    if (isExternal) {
      mappingValues = getFilterMappingValuesWithExternalURL(foundRecords, previewFieldsMapping, url, fieldConfig);
      !mappingValues.success && errorMessage.push(MESSAGE.L02);
    } else {
      mappingValues = getFilterMappingValuesWithInternalURL(foundRecords, previewFieldsMapping, url);
      !mappingValues.success && errorMessage.push(MESSAGE.L02);
      var names = findCatAndReportNameInUrl(url);
      var catName = names.cat;
      var reportName = names.re;

      return validateExistingReport(catName, reportName, uiState.tenantId)
        .then(function(result) {
          !result.data && errorMessage.push(MESSAGE.M11);
          return result.data;
        })
        .then(function(result) {
          if (result) {
            var destinationReportColumns = findColumnInURL(url);

            return validateExistingField(catName, reportName, destinationReportColumns, uiState.tenantId).then(function(
              result
            ) {
              !result.data && errorMessage.push(String.format(MESSAGE.M13, destinationReportColumns.join(',')));
              return errorMessage;
            });
          }
          return errorMessage;
        });
    }

    return Promise.resolve(errorMessage);

    function findColumnInURL(url) {
      // Find p value like {[ShipCountry]}
      const fieldNamesRegex = /{\[(.*?)\]}/g;
      let groups,
        columns = [];
      while ((groups = fieldNamesRegex.exec(url))) {
        columns.push(groups[1]);
      }

      return columns;
    }
  }

  function findCatAndReportNameInUrl(url) {
    var getCatAndReportName = flow(
      split('&'),
      head,
      split('?'),
      last,
      split('='),
      last
    );
    var cateAndReportName = getCatAndReportName(url);
    var cateName = head(cateAndReportName.split('/'));
    var reportName = last(cateAndReportName.split('/'));

    return { cat: cateName === ReportDesignerConstants.UNCATEGORIZED ? null : cateName, re: reportName };
  }

  function getFilterMappingValuesWithExternalURL(records, previewFieldsMapping, url, fieldConfig) {
    var getUrlMappingField = function(url) {
      var charIndex = 0;
      var startIndex = 0;
      var endIndex = 0;
      var length = getOr(0, 'length', url);
      var fieldText = '';
      var fieldMapping = [];

      if (isString(url)) {
        while (charIndex < length) {
          startIndex = indexOfFrom('{', charIndex, url);
          if (startIndex < 0) {
            break;
          }
          endIndex = indexOfFrom('}', startIndex, url);

          if (endIndex < 0) {
            break;
          }
          fieldText = url.substring(startIndex + 1, endIndex);
          if (fieldText === '0') {
            fieldMapping.push({ fieldNameAlias: fieldConfig.fieldElement.fieldNameAlias });
          } else if (startsWith('[', fieldText) && endsWith(']', fieldText)) {
            fieldMapping.push({
              fieldNameAlias: fieldText.substring(1, fieldText.length - 1)
            });
          }
          charIndex = endIndex + 1;
        }
      }
      return fieldMapping;
    };

    return buildFilterDataSubReport(records, getUrlMappingField(url), previewFieldsMapping);
  }

  function getFilterMappingValuesWithInternalURL(records, previewFieldsMapping, url) {
    var getUrlMappingField = flow(
      split('&'),
      tail,
      map(function(item) {
        var splitItem = item.split('=');

        if (splitItem.length === 2) {
          return {
            fieldNameInSubReport:
              splitItem[0].indexOf('{') > -1 ? splitItem[0].substring(1, splitItem[0].length - 1) : splitItem[0],
            fieldNameAlias: splitItem[1].substring(1, splitItem[1].length - 1)
          };
        }
      })
    );

    return buildFilterDataSubReport(records, getUrlMappingField(url), previewFieldsMapping);
  }

  function getContainerByField(containerCollection, field) {
    m.untracked(function() {
      if (!field) {
        return null;
      }
      return filter(function(container) {
        var found = filter(function(item) {
          return item === field;
        });

        return !!found;
      }, containerCollection);
    });
  }

  /**
   * Reset report part setting to default for threshold and xy plane on chart setting
   * @param  {Array} elements  list of elements field
   * @param  {string} key      'threshold or axis'
   */
  function resetDefaultSetting(elements, key) {
    flow(
      flatMap(function(element) {
        return element.properties.otherProps;
      }),
      forEach(function(otherProps) {
        otherProps[key] = undefined;
      })
    )(elements);
  }

  /**
   * Transform cross filter's filter into report part.
   * @param {Object} reportPart
   * @param {Array} filters
   */
  function transformFilterWithReportPart(reportPart: Object, filters: Array) {
    return map(item => {
      let key;
      const sourceAlias = get('field.reportPartElm.field.sourceAlias', item);
      const fieldName = get('field.reportPartElm.field.fieldName', item);
      const functionName = get('field.reportPartElm.properties.dataFormattings.functionInfo.name', item);
      const relationshipId = get('field.reportPartElm.field.relationshipId', item);
      const fieldId = get('field.reportPartElm.field.fieldId', item);
      let finalFieldId = null;

      if (isEmpty(fieldId)) {
        return item;
      }

      if (
        functionName === FUNCTION_GROUP_NAME ||
        isEmpty(get('field.reportPartElm.properties.dataFormattings.functionInfo.id', item))
      ) {
        if (isNil(relationshipId) || relationshipId === EMPTY_GUID) {
          key = null;
          finalFieldId = fieldId;
        } else {
          key = `[${sourceAlias}].[${fieldName}]`;
        }
      } else {
        key = flow(
          find(item => {
            const relationshipIdValue = get('field.relationshipId', item);
            return (
              get('field.fieldName', item) === fieldName &&
              get('field.sourceAlias', item) === sourceAlias &&
              (relationshipIdValue === relationshipId || isEmpty(relationshipIdValue)) &&
              get('properties.dataFormattings.functionInfo.name', item) === functionName
            );
          }),
          get('field.fieldNameAlias')
        )(get('fieldCollection', reportPart));
      }

      let transformedFitler = {
        isCrossFilter: true,
        key: toString(key),
        value: item.value,
        dataFormat: get('field.reportPartElm.properties.dataFormattings.format.formatId', item)
      };

      finalFieldId && (transformedFitler.fieldId = finalFieldId);
      return transformedFitler;
    }, filters);
  }

  /**
   * Get default sort from default report part config.
   * @param {Object} defaultConfig
   * @param {String} reportPartStyle
   * @param {String} containerName
   * @return {String} defaultSort
   */
  function getDefaultSortFromDefaultConfig(defaultConfig: Object, reportPartStyle: String, containerName: String) {
    return flow(
      get(reportPartStyle),
      get('DEFAULT_SORT'),
      get(containerName)
    )(defaultConfig);
  }

  /**
   * Get default sort
   * @param {String} reportPartType
   * @param {String} reportPartStyle
   * @param {String} containerName
   * @return {String} defaultSort
   */
  function getDefaultSort(reportPartType: String, reportPartStyle: String, containerName: String) {
    switch (reportPartType) {
      case ReportDesignerConstants.REPORT_PART_TYPE.Grid:
        return getDefaultSortFromDefaultConfig(GridDefaultConfig.DefaultConfig, reportPartStyle, containerName);
      case ReportDesignerConstants.REPORT_PART_TYPE.Chart:
      case ReportDesignerConstants.REPORT_PART_TYPE.Gauge:
      case ReportDesignerConstants.REPORT_PART_TYPE.Map:
        return getDefaultSortFromDefaultConfig(ChartDefaultConfig.DefaultConfig, reportPartStyle, containerName);
      case ReportDesignerConstants.REPORT_PART_TYPE.Form:
        return getDefaultSortFromDefaultConfig(FormDefaultConfig.DefaultConfig, 'Form', containerName);
      default:
        return 'ASC';
    }
  }

  /**
   * Render lazy loading bar.
   */
  function renderLazyLoadingBar() {
    return <LazyLoadingBar className='u_fullSize u_flex u_flexCenterAlignItems' />;
  }

  /**
   * Check to render lazy loading bar
   * @param {Object} reportPart
   * @param {Boolean} printingMode
   */
  function needToRenderLazyLoadingBar(reportPart: Object, printingMode: Boolean) {
    if (printingMode) {
      return isNil(reportPart);
    } else {
      const isLoadingData = get('isLoadingData', reportPart);
      return isLoadingData === undefined || isLoadingData === true;
    }
  }

  /**
   * Filter format list to display
   * @param {*} reportPartContent
   * @param {*} formats
   * @param {*} isRunningField
   * @param {*} containerName
   */
  function filterFormatForField(
    reportPartContent: any,
    formats: Array<any>,
    isRunningField: boolean,
    containerName: string
  ) {
    const partType = get('type', reportPartContent);
    const isPivotGrid =
      partType === REPORT_PART_TYPE.Grid &&
      get('properties.generalInfo.gridStyle', reportPartContent) === GRID_STYLES.PIVOT.value;
    const isFieldInValuesContainerOfPivotGrid = isPivotGrid && containerName === GRID_CONTAINER_NAME.VALUES;

    const isFieldInColumnsContainerOfPivotGrid = isPivotGrid && containerName === GRID_CONTAINER_NAME.COLUMNS;

    const isInSeparatorContainerOfGrid =
      partType === REPORT_PART_TYPE.Grid && containerName === GRID_CONTAINER_NAME.SEPARATORS;

    return remove(
      (
        format // Remove format for only part types
      ) =>
        (includes(partType, FORMATS_NOT_AVAILABLE_FOR_PART_TYPES.partType) &&
          includes(format.name, FORMATS_NOT_AVAILABLE_FOR_PART_TYPES.formats)) || // Remove format not on running field
        (isRunningField && includes(format.name, FORMATS_NOT_AVAILABLE_FOR_RUNNING_FIELD)) || // Remove format only on pivot
        (!isFieldInValuesContainerOfPivotGrid && includes(format.name, FORMATS_ONLY_ON_PIVOT_GRID_VALUE_CONTAINER)) || // Remove format not available for fields can not set subtotal
        ((isInSeparatorContainerOfGrid || isFieldInColumnsContainerOfPivotGrid) &&
          includes(format.name, FORMAT_NOT_AVAILABLE_ON_FIELD_CAN_NOT_SET_SUBTOTAL)),
      formats
    );
  }

  /**
   * Determine linear type from configField
   * @param {any} configField
   * @param {any} defaultOptionsByType
   */
  function isLinear(configField: any, defaultOptionsByType: Object) {
    // Get type of xAxis
    const xAxisTypeCategory = get('properties.otherProps.axis.izCategory', configField);
    const fieldType = get('previewDataTypeByFunction', configField);
    const chartHaveXYPlaneSetting = defaultOptionsByType.hasOwnProperty('izXYplane');

    return (
      chartHaveXYPlaneSetting &&
      !xAxisTypeCategory &&
      includes(fieldType, ReportPartChartConstants.LIST_COUNTABLE_DATA_TYPE)
    );
  }

  /**
   * check if field in subtotal expressions
   * @param   {Object}  gridStyle             grid style
   * @param   {Object}  container             container
   * @param   {Object}  configField           current selected field
   * @param   {Object}  containerCollection   containerCollection info
   * @return  {Boolean}                       [can field allow unsort]
   */
  function checkCanUnsort(gridStyle, container, configField, containerCollection) {
    var containerName = getOr('', 'name', container);

    switch (gridStyle) {
      case ReportPartGridConstants.GRID_STYLES.VERTICAL.value: // VERTICAL
      case ReportPartGridConstants.GRID_STYLES.HORIZONTAL.value: // HORIZONTAL
        if (containerName === ReportPartGridConstants.CONTAINER_NAME.SEPARATORS) {
          return false;
        }
        if (
          !isUndefined(configField) &&
          isFieldInSubtotalExpression(configField, containerCollection) &&
          getTotalField(containerCollection) > 32
        ) {
          return false;
        }
        return true;
      case ReportPartGridConstants.GRID_STYLES.DRILL_DOWN.value: // DRILL_DOWN
        return false;
      case ReportPartGridConstants.GRID_STYLES.PIVOT.value: // PIVOT
        return containerName === ReportPartGridConstants.CONTAINER_NAME.ROWS;
    }

    return false;
  }

  /**
   * Get total fields of grid
   * @param {Array} containerCollection
   */
  function getTotalField(containerCollection) {
    return flow(
      map(x => x.elements),
      flatMap(observableToArray)
    )(containerCollection).length;
  }

  /**
   * check if field in subtotal expressions
   * @param   {Object}  configField           current selected field
   * @param   {Object}  containerCollection   containerCollection info
   * @return  {Boolean}
   */
  function isFieldInSubtotalExpression(configField, containerCollection) {
    if (configField.hasSubTotal && !isEmpty(configField.properties.dataFormattings.subTotal.function)) {
      return true;
    }
    return flow(
      map(x => x.elements),
      flatMap(observableToArray),
      filter(x => !isUndefined(x) && x.hasSubTotal),
      some(
        // && !isEmpty(x.properties.dataFormattings.function)),
        x =>
          getOr('', 'properties.dataFormattings.subTotal.expression', x)
            .toLowerCase()
            .includes('[' + configField.fieldName.toLowerCase() + ']')
      )
    )(containerCollection);
  }

  /**
   * Return an object contain formated value and original value
   * @param {String} columnName field mapping name
   * @param {Object} record contain record returned form api
   */
  function getFieldValue(columnName: String, record: Object): Object {
    const originalFieldName = ReportPartChartConstants.CUSTOM_FORMAT_PREFIX + columnName;
    const value = get(columnName, record);
    const originalValue = getOr(value, originalFieldName, record);
    return {
      value,
      originalValue
    };
  }

  /**
   * get original value of a field
   * @param {Object} field
   * @param {Object} record
   * @returns {any}
   */
  function getOriginalValue(field: any, record: any) {
    var columnName = get('columnName', field);
    if (isNil(columnName)) {
      return undefined;
    }

    return !isNil(field) && reportPartElementUtils.checkUsingCustomFormat(field)
      ? get(ReportPartChartConstants.CUSTOM_FORMAT_PREFIX + columnName, record)
      : get(columnName, record);
  }

  /**
   * convert array of observable to array
   * @param   {ObservableArray}  observable   current selected field
   * @return  {Array}                         [container with elements typeof ReportPartElement]
   */
  const observableToArray = curry(observable => {
    var arrResult = [];
    observable.forEach(item => arrResult.push(item));
    return arrResult;
  });

  module.exports = {
    getAlternativeText: getAlternativeText,
    getDefaultFunction: function(soure, type) {
      return get(type || '', soure) || get('Others', soure);
    },
    getFieldValue,
    getOriginalValue,
    /**
     * Get data format of value based on its config
     * @type {[type]}
     */
    getDataFormat: getDataFormat,
    applyFormater: applyFormater,
    getConfigDataByRange: getConfigDataByRange,
    getFormatInfo: getFormatData,
    getDefaultFormat: getDefaultFormat,
    openHyperlinkAction: openHyperlinkAction,
    openCustomURL: openCustomURL,
    performEmbeddedJavascript: performEmbeddedJavascript,
    getDefaultFunctionFormat: getDefaultFunctionFormat,
    convertNonFunctionElementsToFunction: convertNonFunctionElementsToFunction,
    setDefaultFunctionElement: setDefaultFunctionElement,
    setFunctionElement: setFunctionElement,
    addContainers: addContainers,
    addCustomContainers: addCustomContainers,
    hasElement: hasElement,
    checkedCompatibleFunctionHasAggregatedFunction: checkedCompatibleFunctionHasAggregatedFunction,
    checkCompatibleFunctionCommon: checkCompatibleFunctionCommon,
    hasAtLeastElement: hasAtLeastElement,
    getElements: getElements,
    getUniqueName: getUniqueName,
    buildFilterDataSubReport: buildFilterDataSubReport,
    populateReportChartContainer: populateReportChartContainer,
    populateReportChartCustomContainer: populateReportChartCustomContainer,
    buildMappingFields: buildMappingFields,
    validateURL: validateURL,
    buildDataSourceElement: buildDataSourceElement,
    findCatAndReportNameInUrl: findCatAndReportNameInUrl,
    getContainerByField: getContainerByField,
    resetDefaultSetting: resetDefaultSetting,
    transformFilterWithReportPart: transformFilterWithReportPart,
    getDefaultSort: getDefaultSort,
    renderLazyLoadingBar: renderLazyLoadingBar,
    needToRenderLazyLoadingBar: needToRenderLazyLoadingBar,
    filterFormatForField: filterFormatForField,
    isLinear: isLinear,
    checkCanUnsort: checkCanUnsort,
    getFunction: getFunction
  };
})();



// WEBPACK FOOTER //
// ./ui/share/utils/Izenda.ReportPartUtils.js